import {BehaviorSubject, Subject, combineLatest, of} from 'rxjs';
import {filter, skipUntil, switchMap, debounceTime, tap} from 'rxjs/operators';
import {parse, stringify} from 'query-string';
import {get} from 'svelte/store';
import {mc} from '@/core';
import {buyCrypto} from '@/api/buy-crypto';
import {kycApi} from '@/api/kyc';
import {legendTradingApi} from '@/api/legend-trading';
import {env} from '@/helper/env';
import {lang$} from '@/store/lang';
import {browser$} from '@/streams/browser';
import {userStatus$} from '@/streams/auth';
import {countries$, getCountries$} from '@/modules/popups/basic-kyc/streams';
import {OtcKycCode} from './const';

const langMap = {cn: 'zh'};

export const isLoading$ = new BehaviorSubject(false);
export const loadBuyCryptoConfig$ = new Subject();
export const loadBuyCryptoPricing$ = new Subject();
export const loadBuyCryptoBanner$ = new Subject();
export const loadRegions$ = new Subject();
export const buyCryptoConfig$ = new BehaviorSubject(null);
export const buyCryptoPricing$ = new Subject();
export const buyCryptoBanner$ = new Subject();
export const regions$ = new Subject();
export const isInitLegendTradingKycStatus$ = new BehaviorSubject(true);
export const loadLegendTradingKycVerify$ = new Subject();
export const needReplaceOTCSdk$ = new BehaviorSubject(false);
function calcLang(lang) {
  return langMap[lang] || 'en';
}

export async function initLegend() {
  const {data, error} = await legendTradingApi.getParams();
  if (data) {
    const {timestamp, signature, appId, appUrl, appKey, appUid, mode, appEmail} = data;
    const {origin} = location;
    const assetsUrl = `${origin}/assets/deposit`;
    const depositUrl = `${origin}/assets/deposit?currency={{SYMBOL}}`;
    const lang = calcLang(get(lang$));
    const attrs = {
      timestamp,
      signature,
      'app-id': appId,
      'app-uid': appUid,
      'app-url': appUrl,
      'app-key': appKey,
      'url-assets': assetsUrl,
      'url-deposit': depositUrl,
      'app-email': appEmail,
      lang,
      mode: !env.isProduction ? 'sandbox' : mode,
    };

    return Object.keys(attrs)
      .reduce((arr, key) => [...arr, `${key}="${attrs[key]}"`], [])
      .join(' ');
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

export async function getIsForbidden() {
  const {data, error} = await legendTradingApi.checkGeo();
  if (data) {
    return data.forbidden;
  }
  if (error) {
    mc.send('toast', 'error', error);
    return false;
  }
}

combineLatest([loadBuyCryptoConfig$, userStatus$])
  .pipe(
    skipUntil(browser$),
    tap(() => isLoading$.next(true)),
    debounceTime(500),
    switchMap(([query, userStatus]) => getBuyCryptoConfig(query, userStatus)),
    filter(Boolean)
  )
  .subscribe((res) => {
    isLoading$.next(false);
    buyCryptoConfig$.next(res);
  });

combineLatest([loadBuyCryptoPricing$, userStatus$])
  .pipe(
    skipUntil(browser$),
    tap(() => isLoading$.next(true)),
    debounceTime(500),
    switchMap(([query, userStatus]) => getBuyCryptoPricing(query, userStatus)),
    filter(Boolean)
  )
  .subscribe((res) => {
    isLoading$.next(false);
    const {isUpdate} = res;
    const [pricingInfo] = res.channels;
    buyCryptoPricing$.next({...pricingInfo, isUpdate});
  });

loadBuyCryptoBanner$.pipe(skipUntil(browser$), switchMap(getBuyCryptoBanner), filter(Boolean)).subscribe((res) => {
  buyCryptoBanner$.next(res);
});

export async function getBuyCryptoConfig(query, userStatus) {
  if (userStatus && userStatus === 'Init') {
    return;
  }
  const isLogin = userStatus === 'Login';
  const {data, error} = await buyCrypto.getBuyCryptoConfig(query, isLogin);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

export async function getBuyCryptoPricing(query, userStatus) {
  if (userStatus && userStatus === 'Init') {
    return;
  }
  const isLogin = userStatus === 'Login';
  const {isUpdate, ...q} = query;
  const config = buyCryptoConfig$.getValue();
  if (!config) {
    return;
  }
  const isCorrectRange =
    q.side === 'buy'
      ? +config.minBuyFiatAmount <= q.fiatAmount && +config.maxBuyFiatAmount >= q.fiatAmount
      : +config.minSellDigitalAmount <= q.digitalAmount && +config.maxSellDigitalAmount >= q.digitalAmount;

  if (!isCorrectRange) {
    return null;
  }

  const {data, error} = await buyCrypto.getBuyCryptoPricing(q, isLogin);
  if (data) {
    return {...data, isUpdate};
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

export async function getBuyCryptoBanner(query) {
  const {data, error} = await buyCrypto.getBuyCryptoBanner(query);
  if (data) {
    const banner = data.rows.find((row) => row.language === query.language);
    if (banner) {
      return banner;
    }
    if (data.rows.length === 0) {
      return null;
    }
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

export async function checkKyc() {
  const {data, error, reply} = await legendTradingApi.checkKyc();
  if (data === true) {
    return 'Pass';
  }
  if (OtcKycCode[reply.code]) {
    return OtcKycCode[reply.code];
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

export function getCountryRegions() {
  loadRegions$.next();
  getCountries$.next();
}

loadRegions$.pipe(skipUntil(browser$), switchMap(getRegions), filter(Boolean)).subscribe((res) => {
  regions$.next(res);
});

export async function getRegions() {
  const {data, error} = await kycApi.getCountryRegions();
  if (data) {
    return data.rows;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

export const countriesRegions$ = combineLatest([countries$, regions$]);

loadLegendTradingKycVerify$
  .pipe(
    switchMap(() => {
      const searchParam = parse(window.location.search);
      if (['amount', 'payment'].every((key) => Boolean(searchParam[key]))) {
        return of(false);
      }
      return loadLegendTradingKycVerify();
    })
  )
  .subscribe((needReplace) => {
    if (needReplace) {
      needReplaceOTCSdk$.next(true);
    }
    isInitLegendTradingKycStatus$.next(false);
  });

async function loadLegendTradingKycVerify() {
  const {data, error} = await legendTradingApi.getKycApprovedStatus();
  if (error) {
    mc.send('toast', 'error', error);
  }
  return data;
}
